import withApplicationPropsV2 from '@/creditflowresearch/application/withApplicationPropsV2';
import getServerSideArticles
  from '@/creditflowresearch/application/ArticlesAndStats/Articles/serverSide/getServerSideArticles';
import { PageContentLayout } from '@/creditflowresearch/ui/common/components/ContentLayouts';
import getServerSideArticlesBreadcrumbs
  from '@/creditflowresearch/application/Breadcrumbs/serverSide/getServerSideArticlesBreadcrumbs';
import getQueryParamsWithProductsRegions
  from '@/creditflowresearch/application/helpers/getQueryParamsWithProductsRegions';
import PageFiltersContextProvider from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { pageRedirectTo } from '@framework/router/Next/helpers';
import { Product } from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import RegionBasedArticles from '@/creditflowresearch/ui/components/creditFlow/articles/RegionBasedArticles';
import CorporateMarketArticles from '@/creditflowresearch/ui/components/creditFlow/articles/CorporateMarketArticles';
import CorpSegment from '@/creditflowresearch/domain/Segment/entities/CorpSegment';
import EuropeanMarket from '@/creditflowresearch/domain/Market/entities/EuropeanMarket';
import {
  getDefaultCorporateSegments,
} from '@/creditflowresearch/application/helpers/getCorporatePageDefaultParams';
import { IMarketArticlesData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import getDefaultQueryFilters from '@/creditflowresearch/application/helpers/getDefaultQueryFilters';
import { corporateValidators } from '@/creditflowresearch/application/helpers/validators';
import { removeQueryParams } from '@framework/router';

export const getServerSideProps = withApplicationPropsV2(async (context) => {
  const { productIds = [], regionIds = [] } = context.productsRegions || {};
  const defaultProducts: string[] = productIds.map((id) => Product.getAbbreviation(id)).filter(Boolean) as string[];
  const defaultRegions: string[] = regionIds.map((id) => Region.getAbbreviation(id)).filter(Boolean) as string[];

  const hasMarketsInQuery = Boolean(context.query.markets);
  const usesRegionBasedFilters = !hasMarketsInQuery;

  if (hasMarketsInQuery) {
    const sanitized = getDefaultQueryFilters({
      query: removeQueryParams(context.query, ['articleType', 'productType']),
      defaults: { products: defaultProducts.join(','), markets: EuropeanMarket.ABBREVIATION, segments: CorpSegment.TYPE },
      validators: corporateValidators,
    });

    if (sanitized) {
      return pageRedirectTo(sanitized);
    }
  }

  const requiredParams = usesRegionBasedFilters
    ? { products: defaultProducts, regions: defaultRegions }
    : {
      products: defaultProducts, markets: EuropeanMarket.ABBREVIATION,
      segments: getDefaultCorporateSegments(context.query.products || defaultProducts),
    };

  const queryParams = getQueryParamsWithProductsRegions(
    context.query,
    ['articleType', 'productType'],
    requiredParams,
  );

  if (queryParams) {
    return pageRedirectTo(queryParams);
  }

  const articlesData = await getServerSideArticles(context);

  const breadcrumbs = getServerSideArticlesBreadcrumbs(context.query);

  return {
    props: {
      articlesData,
      breadcrumbs,
      usesRegionBasedFilters,
    },
  };
}, { needsLegacyDictionaries: false });

interface IArticlesPageProps {
  articlesData: IMarketArticlesData,
  usesRegionBasedFilters: boolean,
}

const ArticlesPage = ({ articlesData, usesRegionBasedFilters }: IArticlesPageProps) => {
  return (
    <PageFiltersContextProvider>
      <PageContentLayout>
        { usesRegionBasedFilters ? (
          <RegionBasedArticles articlesData={ articlesData } />
        ) : (
          <CorporateMarketArticles articlesData={ articlesData } />
        ) }
      </PageContentLayout>
    </PageFiltersContextProvider>
  );
};

export default ArticlesPage;
